import { useApp, useTranslation } from "@next-platform/client-common-core";
import clientClipboard from "@next-platform/client-common-core/services/clipboard";
import { Icon, IconSize, IconVariants, Modal } from "@next-platform/client-common-spicy-ui";
import { JSX, useCallback } from "react";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  FacebookShareButton,
  RedditIcon,
  RedditShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";

import { Row, SpicyButton, SpicyInput, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";

const SHARE_MODAL_CLASS_NAMES = { title: "text-center pl-[28px]" };
const SHARE_URL_INPUT_CLASS_NAMES = { input: "text-[14px]" };
export type ShareModalProps = {
  title?: string;
  shareUrl?: string;
  instructions?: string;
  shareMessage?: string;
  visible: boolean;
  setShowShare: (visible: boolean) => void;
  characterId: string | undefined;
  characterName: string | undefined;
};

function ShareModal({
  visible,
  setShowShare,
  characterId,
  characterName,
  title,
  shareUrl: shareUrlProp,
  instructions,
  shareMessage: shareMessageProp,
}: ShareModalProps): JSX.Element {
  const { applicationName } = useApp();
  const { t } = useTranslation();
  const currentUrlOrigin = window.location.origin;
  const shareUrl = shareUrlProp || `${currentUrlOrigin}/chat/${characterId}`;
  const shareMessage = shareMessageProp || t("share:chatbot.modal.message", { characterName, applicationName, shareUrl });
  const handleCopyToClipboard = useCallback(() => {
    clientClipboard
      .writeText(shareUrl)
      .then(() => {
        setShowShare(false);
      })
      .catch(clientClipboard.defaultCatch);
  }, [shareUrl, setShowShare]);

  return (
    <>
      <Modal open={visible} onOpenChange={setShowShare} title={title || t("share:chatbot.modal.title")} classNames={SHARE_MODAL_CLASS_NAMES}>
        <SpicyInput variant="primary_bordered" color="secondary" isReadOnly value={shareUrl} classNames={SHARE_URL_INPUT_CLASS_NAMES} />
        <Text variant={"sm"}>{instructions ? instructions : <Trans i18nKey="share:chatbot.modal.description" />}</Text>
        <SpicyButton color="primary_strong" onPress={handleCopyToClipboard} startContent={<Icon icon={IconVariants.COPY} size={IconSize.SMALL} />}>
          <b className="font-bold">
            <Trans i18nKey="share:chatbot.modal.cta.copy" />
          </b>
        </SpicyButton>
        <Row className="overflow-x-auto justify-between">
          <FacebookShareButton url={shareUrl} title={shareMessage}>
            <FacebookIcon size={38} round />
          </FacebookShareButton>

          <TwitterShareButton url={shareUrl} title={shareMessage}>
            <TwitterIcon size={38} round />
          </TwitterShareButton>

          <EmailShareButton url={shareUrl} title={shareMessage}>
            <EmailIcon size={38} round />
          </EmailShareButton>

          <RedditShareButton url={shareUrl} title={shareMessage}>
            <RedditIcon size={38} round />
          </RedditShareButton>

          <TelegramShareButton url={shareUrl} title={shareMessage}>
            <TelegramIcon size={38} round />
          </TelegramShareButton>

          <WhatsappShareButton url={shareUrl} title={shareMessage}>
            <WhatsappIcon size={38} round />
          </WhatsappShareButton>

          {/* @ts-expect-error wants appId */}
          <FacebookMessengerShareButton url={shareUrl} title={shareMessage}>
            <FacebookMessengerIcon size={38} round />
          </FacebookMessengerShareButton>
        </Row>
      </Modal>
    </>
  );
}

export default ShareModal;
